import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { isUser, isAdmin, isStaff } from '../../features/auth/authSlice';
import api from '../../utils/axiosConfig';
import { formatLogoUrl } from '../../utils/imageUtils';
import {
    ArrowLeftIcon,
    ClipboardDocumentListIcon,
    UserGroupIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    DocumentTextIcon,
    CloudArrowUpIcon,
    ExclamationTriangleIcon,
    ChartBarIcon,
    PlusIcon,
    XMarkIcon,
    CurrencyDollarIcon,
    CheckCircleIcon,
    ClockIcon,
    LinkIcon,
    ClipboardIcon
} from '@heroicons/react/24/outline';

interface TaskDetails {
    _id: string;
    title: string;
    description: string;
    taskUrl: string;
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    postedBy: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    budgetDistribution: {
        teamExecutive: number;
        teamLeader: number;
        districtHead: number;
        stateHead: number;
        regionalHead: number;
        admin: number;
    };
    budgetScenarios: Array<{
        _id: string;
        name: string;
        range: {
            min: number;
            max: number;
        };
        budgetDistribution: {
            teamExecutive: number;
            teamLeader: number;
            districtHead: number;
            stateHead: number;
            regionalHead: number;
            admin: number;
        };
        isActive: boolean;
    }>;
    requiredSkills: Array<{
        _id: string;
        name: string;
    }>;
    status: string;
    priority: string;
    startDate: string;
    endDate: string;
    maxParticipants: number;
    currentParticipants: number;
    dataCollectionCriteria: string;
    validationRules: {
        requiredFields: string[];
        optionalFields: string[];
    };
    assignedUsers: Array<{
        user?: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            designation: string;
            region: string;
            personalDetails: {
                address: {
                    state: string;
                    district: string;
                };
            };
        };
        staff?: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            role: string;
            designation: string;
            isActive: boolean;
        };
        assignedAt: string;
        status: string;
        designation: string;
        referralId?: string;
        referralLink?: string;
        myReferralLink?: string;
        dataSubmitted: any[];
        earnings: {
            totalEarned: number;
            validatedEarnings: number;
            pendingEarnings: number;
        };
    }>;
    createdAt: string;
    updatedAt: string;
}


const UserTaskDetails: React.FC = () => {
    const { taskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);


    const [task, setTask] = useState<TaskDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [showDataSubmissionModal, setShowDataSubmissionModal] = useState(false);
    const [submissionData, setSubmissionData] = useState<any[]>([]);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [showCsvPreview, setShowCsvPreview] = useState(false);
    const [copiedLink, setCopiedLink] = useState<string | null>(null);

    const copyToClipboard = async (text: string, linkId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedLink(linkId);
            setTimeout(() => setCopiedLink(null), 2000);
            dispatch(showToast({ message: 'Referral link copied to clipboard!', type: 'success' }));
        } catch (err) {
            console.error('Failed to copy:', err);
            dispatch(showToast({ message: 'Failed to copy link', type: 'error' }));
        }
    };

    // Calculate earnings based on budget scenarios and user data
    const calculateEarnings = useCallback((task: TaskDetails, userAssignment: any) => {
        if (!task || !userAssignment || !task.budgetScenarios) {
            return { totalEarned: 0, validatedEarnings: 0, pendingEarnings: 0 };
        }

        const dataSubmitted = userAssignment.dataSubmitted || [];
        const currentDataCount = dataSubmitted.length;

        let totalEarnings = 0;
        let validatedEarnings = 0;
        let pendingEarnings = 0;

        // Map designation to budget distribution key
        const designationMap: { [key: string]: string } = {
            'team_executive': 'teamExecutive',
            'team_leader': 'teamLeader',
            'district_head': 'districtHead',
            'state_head': 'stateHead',
            'regional_head': 'regionalHead',
            'admin': 'admin'
        };

        const budgetKey = designationMap[userAssignment.designation] || userAssignment.designation;

        // Calculate earnings for each data entry based on budget scenarios and validation status
        for (let i = 0; i < dataSubmitted.length; i++) {
            const dataEntry = dataSubmitted[i];
            const dataIndex = i + 1; // 1-based index

            // Find the appropriate budget scenario for this data index
            const applicableScenario = task.budgetScenarios.find((scenario: any) =>
                scenario.isActive &&
                dataIndex >= scenario.range.min &&
                dataIndex <= scenario.range.max
            );

            if (applicableScenario) {
                const budgetDistribution = applicableScenario.budgetDistribution;
                const earningsPerEntry = (budgetDistribution as any)[budgetKey] || 0;

                totalEarnings += earningsPerEntry;

                // Check if data is validated/approved
                if (dataEntry.isValidated === true) {
                    validatedEarnings += earningsPerEntry;
                } else {
                    pendingEarnings += earningsPerEntry;
                }
            }
        }

        return {
            totalEarned: totalEarnings,
            validatedEarnings: validatedEarnings,
            pendingEarnings: pendingEarnings
        };
    }, []);

    const fetchTaskDetails = useCallback(async () => {
        if (!taskId) return;

        try {
            setLoading(true);
            const response = await api.get(`/tasks/${taskId}`);

            if (response.data.success) {
                console.log('Task data received:', response.data.data);
                setTask(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching task details:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch task details',
                type: 'error'
            }));
            navigate('/dashboard/tasks');
        } finally {
            setLoading(false);
        }
    }, [taskId, dispatch, navigate]);


    useEffect(() => {
        fetchTaskDetails();
    }, [fetchTaskDetails]);

    const handleDataSubmission = async () => {
        if (!taskId || !user || !isUser(user)) return;

        console.log('Submitting data:', submissionData);

        try {
            const response = await api.post(`/tasks/${taskId}/submit-data-user`, {
                data: submissionData
            });

            if (response.data.success) {
                dispatch(showToast({
                    message: 'Data submitted successfully!',
                    type: 'success'
                }));
                setShowDataSubmissionModal(false);
                setSubmissionData([]);
                fetchTaskDetails(); // Refresh task details
            }
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to submit data',
                type: 'error'
            }));
        }
    };

    const addDataRow = () => {
        if (task?.validationRules?.requiredFields && task.validationRules.requiredFields.length > 0) {
            const newRow: any = {};
            task.validationRules.requiredFields.forEach(field => {
                newRow[field] = '';
            });
            setSubmissionData([...submissionData, newRow]);
        }
    };

    const updateDataRow = (index: number, field: string, value: string) => {
        const updatedData = [...submissionData];
        updatedData[index][field] = value;
        setSubmissionData(updatedData);
    };

    const removeDataRow = (index: number) => {
        const updatedData = submissionData.filter((_, i) => i !== index);
        setSubmissionData(updatedData);
    };

    const handleCsvFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            dispatch(showToast({
                message: 'Please select a valid CSV file',
                type: 'error'
            }));
            return;
        }

        setCsvFile(file);
        parseCsvFile(file);
    };

    const parseCsvFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());

            if (lines.length < 2) {
                dispatch(showToast({
                    message: 'CSV file must have at least a header row and one data row',
                    type: 'error'
                }));
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const requiredFields = task?.validationRules?.requiredFields || [];

            // Check if all required fields are present
            const missingFields = requiredFields.filter(field => !headers.includes(field));
            if (missingFields.length > 0) {
                dispatch(showToast({
                    message: `CSV file is missing required fields: ${missingFields.join(', ')}`,
                    type: 'error'
                }));
                return;
            }

            const parsedData = lines.slice(1).map(line => {
                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                const row: any = {};
                headers.forEach((header, index) => {
                    if (requiredFields.includes(header)) {
                        row[header] = values[index] || '';
                    }
                });
                return row;
            }).filter(row => Object.values(row).some(value => value !== ''));

            setCsvData(parsedData);
            setShowCsvPreview(true);

            dispatch(showToast({
                message: `Successfully parsed ${parsedData.length} rows from CSV file`,
                type: 'success'
            }));
        };
        reader.readAsText(file);
    };

    const useCsvData = () => {
        setSubmissionData(csvData);
        setCsvFile(null);
        setCsvData([]);
        setShowCsvPreview(false);
        dispatch(showToast({
            message: 'CSV data loaded successfully. You can now edit and submit.',
            type: 'success'
        }));
    };

    const clearCsvData = () => {
        setCsvFile(null);
        setCsvData([]);
        setShowCsvPreview(false);
    };


    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Calculate hierarchical earnings based on data distribution (same as admin)
    const calculateHierarchicalEarnings = () => {
        if (!task) return {};

        const budgetDistribution = task.budgetDistribution || {};
        const earnings: { [key: string]: number } = {};

        // Group users by their hierarchical levels
        const stateHeads = task.assignedUsers.filter(u => u.user?.designation === 'state_head');
        const districtHeads = task.assignedUsers.filter(u => u.user?.designation === 'district_head');
        const teamLeaders = task.assignedUsers.filter(u => u.user?.designation === 'team_leader');
        const teamExecutives = task.assignedUsers.filter(u => u.user?.designation === 'team_executive');

        // Calculate earnings for each user based on their hierarchical data
        task.assignedUsers.forEach(assignment => {
            if (!assignment.user) return;
            const userId = assignment.user._id;
            const designation = assignment.user.designation;
            const userDataCount = assignment.dataSubmitted?.length || 0;

            let totalEarnings = 0;

            if (designation === 'team_executive') {
                // Team executives get earnings for their own data
                totalEarnings = userDataCount * (budgetDistribution.teamExecutive || 0);
            } else if (designation === 'team_leader') {
                // Team leaders get earnings for their own data + percentage of their team's data
                const ownDataEarnings = userDataCount * (budgetDistribution.teamLeader || 0);
                const teamDataEarnings = calculateTeamDataEarnings(assignment.user, teamExecutives, budgetDistribution.teamLeader || 0);
                totalEarnings = ownDataEarnings + teamDataEarnings;
            } else if (designation === 'district_head') {
                // District heads get earnings for their own data + percentage of district data
                const ownDataEarnings = userDataCount * (budgetDistribution.districtHead || 0);
                const districtDataEarnings = calculateDistrictDataEarnings(assignment.user, teamLeaders, teamExecutives, budgetDistribution.districtHead || 0);
                totalEarnings = ownDataEarnings + districtDataEarnings;
            } else if (designation === 'state_head') {
                // State heads get earnings for their own data + percentage of state data
                const ownDataEarnings = userDataCount * (budgetDistribution.stateHead || 0);
                const stateDataEarnings = calculateStateDataEarnings(assignment.user, districtHeads, teamLeaders, teamExecutives, budgetDistribution.stateHead || 0);
                totalEarnings = ownDataEarnings + stateDataEarnings;
            } else if (designation === 'regional_head') {
                // Regional heads get earnings for their own data + percentage of regional data
                const ownDataEarnings = userDataCount * (budgetDistribution.regionalHead || 0);
                const regionalDataEarnings = calculateRegionalDataEarnings(assignment.user, stateHeads, districtHeads, teamLeaders, teamExecutives, budgetDistribution.regionalHead || 0);
                totalEarnings = ownDataEarnings + regionalDataEarnings;
            }

            earnings[userId] = totalEarnings;
        });

        return earnings;
    };

    // Helper functions for hierarchical earnings calculation
    const calculateTeamDataEarnings = (teamLeader: any, teamExecutives: any[], rate: number) => {
        // Find team executives under this team leader (same district/state)
        const teamMembers = teamExecutives.filter(exec =>
            exec.user.personalDetails?.address?.district === teamLeader.personalDetails?.address?.district &&
            exec.user.personalDetails?.address?.state === teamLeader.personalDetails?.address?.state
        );

        const totalTeamData = teamMembers.reduce((sum, member) => sum + (member.dataSubmitted?.length || 0), 0);
        return totalTeamData * rate * 0.1; // 10% of team data earnings
    };

    const calculateDistrictDataEarnings = (districtHead: any, teamLeaders: any[], teamExecutives: any[], rate: number) => {
        // Find all users under this district
        const districtUsers = [...teamLeaders, ...teamExecutives].filter(user =>
            user.user.personalDetails?.address?.district === districtHead.personalDetails?.address?.district &&
            user.user.personalDetails?.address?.state === districtHead.personalDetails?.address?.state
        );

        const totalDistrictData = districtUsers.reduce((sum, user) => sum + (user.dataSubmitted?.length || 0), 0);
        return totalDistrictData * rate * 0.15; // 15% of district data earnings
    };

    const calculateStateDataEarnings = (stateHead: any, districtHeads: any[], teamLeaders: any[], teamExecutives: any[], rate: number) => {
        // Find all users under this state
        const stateUsers = [...districtHeads, ...teamLeaders, ...teamExecutives].filter(user =>
            user.user.personalDetails?.address?.state === stateHead.personalDetails?.address?.state
        );

        const totalStateData = stateUsers.reduce((sum, user) => sum + (user.dataSubmitted?.length || 0), 0);
        return totalStateData * rate * 0.2; // 20% of state data earnings
    };

    const calculateRegionalDataEarnings = (regionalHead: any, stateHeads: any[], districtHeads: any[], teamLeaders: any[], teamExecutives: any[], rate: number) => {
        // Find all users under this region
        const regionalUsers = [...stateHeads, ...districtHeads, ...teamLeaders, ...teamExecutives].filter(user =>
            user.user.region === regionalHead.region
        );

        const totalRegionalData = regionalUsers.reduce((sum, user) => sum + (user.dataSubmitted?.length || 0), 0);
        return totalRegionalData * rate * 0.25; // 25% of regional data earnings
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
            case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200';
            case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200';
            case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200';
            case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200';
            case 'low': return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading task details...</p>
                </div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Task Not Found</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">The task you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/dashboard/tasks')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/dashboard/tasks')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Back to Tasks
                    </button>

                    <div className="flex items-start justify-between">
                        <div className="flex items-start">
                            <ClipboardDocumentListIcon className="h-10 w-10 text-blue-600 mr-4 mt-1" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{task.title}</h1>
                                <div className="flex items-center space-x-4">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
                                        {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(task.priority)}`}>
                                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Data Submission Button for Team Executives */}
                        {user && isUser(user) && user.designation === 'team_executive' && (
                            <button
                                onClick={() => setShowDataSubmissionModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                                <CloudArrowUpIcon className="h-4 w-4" />
                                Submit Data
                            </button>
                        )}
                    </div>
                </div>

                {/* Referral Link Banner */}
                {(() => {
                    const userAssignment = user ? task.assignedUsers.find(au =>
                        (au.user?._id === user.id) || (au.staff?._id === user.id)
                    ) : null;
                    const referralLink = userAssignment?.myReferralLink || userAssignment?.referralLink;
                    const userDesignation = userAssignment?.designation || userAssignment?.user?.designation || (user && isUser(user) ? user.designation : null);
                    const isTeamExecutive = userDesignation === 'team_executive';

                    if (referralLink && isTeamExecutive) {
                        return (
                            <div className="mb-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                                        <div className="flex-shrink-0">
                                            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
                                                <LinkIcon className="h-6 w-6 text-white" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold text-white mb-1">Your Referral Link</h3>
                                            <p className="text-sm text-blue-100 mb-2">Share this link to track referrals and earn rewards</p>
                                            <div className="flex items-center space-x-2 bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                                                <p className="flex-1 text-xs text-white font-mono truncate">
                                                    {referralLink}
                                                </p>
                                                <button
                                                    onClick={() => copyToClipboard(referralLink, `banner-${task._id}`)}
                                                    className="ml-2 inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium flex-shrink-0"
                                                >
                                                    {copiedLink === `banner-${task._id}` ? (
                                                        <>
                                                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ClipboardIcon className="h-4 w-4 mr-1" />
                                                            Copy Link
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })()}

                {/* Tabs */}
                <div className="mb-8">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
                            { id: 'hierarchy', label: 'Team Hierarchy', icon: UserGroupIcon },
                            { id: 'data', label: 'Data Collection', icon: ChartBarIcon }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab.id
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4 mr-2" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
                    {activeTab === 'overview' && (
                        <div className="p-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Basic Information */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                                        Basic Information
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">{task.description}</p>
                                        </div>
                                        {(user && (isAdmin(user) || isStaff(user))) && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Task URL</label>
                                                <a
                                                    href={task.taskUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    {task.taskUrl}
                                                </a>
                                            </div>
                                        )}
                                        {(() => {
                                            const userAssignment = user ? task.assignedUsers.find(au =>
                                                (au.user?._id === user.id) || (au.staff?._id === user.id)
                                            ) : null;
                                            const referralLink = userAssignment?.myReferralLink || userAssignment?.referralLink;
                                            const userDesignation = userAssignment?.designation || userAssignment?.user?.designation || (user ? (isUser(user) ? user.designation : null) : null);
                                            const isTeamExecutive = userDesignation === 'team_executive';

                                            if (referralLink && isTeamExecutive) {
                                                return (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Your Referral Link
                                                        </label>
                                                        <div className="mt-1 flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                                                            <LinkIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                            <p className="flex-1 text-xs text-blue-700 dark:text-blue-300 font-mono truncate">
                                                                {referralLink}
                                                            </p>
                                                            <button
                                                                onClick={() => copyToClipboard(referralLink, `task-${task._id}`)}
                                                                className="ml-2 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs flex-shrink-0"
                                                            >
                                                                {copiedLink === `task-${task._id}` ? (
                                                                    <>
                                                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                        Copied!
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ClipboardIcon className="h-4 w-4 mr-1" />
                                                                        Copy
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                            Share this link to track referrals for this task
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                                            <div className="mt-1 flex items-center">
                                                {task.company.logo && (
                                                    <img
                                                        src={formatLogoUrl(task.company.logo)}
                                                        alt={task.company.companyName}
                                                        className="h-6 w-6 rounded mr-2"
                                                    />
                                                )}
                                                <span className="text-gray-900 dark:text-white">{task.company.companyName}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Posted By</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {task.postedBy.firstName} {task.postedBy.lastName}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                                        Timeline
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {formatDate(task.startDate)}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                                            <p className="mt-1 text-gray-900 dark:text-white">
                                                {formatDate(task.endDate)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Requirements */}
                            <div className="mt-8">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <ClipboardDocumentListIcon className="h-5 w-5 text-purple-600 mr-2" />
                                    Requirements
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Required Skills</label>
                                        <div className="flex flex-wrap gap-2">
                                            {task.requiredSkills?.map((skill) => (
                                                <span key={skill._id} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full text-sm">
                                                    {skill.name}
                                                </span>
                                            )) || []}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Earnings Section */}
                            {(() => {
                                const userAssignment = user ? task.assignedUsers.find(au => au.user?._id === user.id) : null;

                                if (!userAssignment) {
                                    return null;
                                }

                                const currentUserDesignation = userAssignment.user?.designation;
                                const currentUserRegion = userAssignment.user?.region;
                                const currentUserAddress = userAssignment.user?.personalDetails?.address;

                                let calculatedEarnings = { totalEarned: 0, validatedEarnings: 0, pendingEarnings: 0 };
                                let dataCount = 0;
                                let earningsDescription = '';

                                if (currentUserDesignation === 'team_executive') {
                                    // Team executives only see their own data multiplied by their budget scenario amount
                                    calculatedEarnings = calculateEarnings(task, userAssignment);
                                    dataCount = userAssignment.dataSubmitted?.length || 0;
                                    earningsDescription = `Based on your ${dataCount} data entries • TEAM EXECUTIVE designation`;
                                } else {
                                    // Other hierarchical roles see earnings from team executives in their jurisdiction
                                    const applicableTeamExecutives = task.assignedUsers.filter(assignment => {
                                        const teUser = assignment.user;
                                        const teDesignation = teUser?.designation;

                                        // Must be team executive with data submitted
                                        if (teDesignation !== 'team_executive' || (assignment.dataSubmitted?.length || 0) === 0) {
                                            return false;
                                        }

                                        // Location matching based on current user's designation
                                        if (currentUserDesignation === 'team_leader' || currentUserDesignation === 'district_head') {
                                            // Match district + state
                                            return teUser?.personalDetails?.address?.district === currentUserAddress?.district &&
                                                teUser?.personalDetails?.address?.state === currentUserAddress?.state;
                                        } else if (currentUserDesignation === 'state_head') {
                                            // Match state + region
                                            return teUser?.personalDetails?.address?.state === currentUserAddress?.state &&
                                                teUser?.region === currentUserRegion;
                                        } else if (currentUserDesignation === 'regional_head') {
                                            // Match region only
                                            return teUser?.region === currentUserRegion;
                                        }

                                        return false;
                                    });

                                    // Calculate total earnings from applicable team executives using current user's budget scenario
                                    const designationMap: { [key: string]: string } = {
                                        'team_executive': 'teamExecutive',
                                        'team_leader': 'teamLeader',
                                        'district_head': 'districtHead',
                                        'state_head': 'stateHead',
                                        'regional_head': 'regionalHead',
                                        'admin': 'admin'
                                    };

                                    const currentUserBudgetKey = designationMap[currentUserDesignation || ''] || currentUserDesignation || 'teamExecutive';

                                    for (const teAssignment of applicableTeamExecutives) {
                                        const teDataSubmitted = teAssignment.dataSubmitted || [];

                                        // Calculate earnings for each data entry using current user's budget scenario
                                        for (let i = 0; i < teDataSubmitted.length; i++) {
                                            const dataEntry = teDataSubmitted[i];
                                            const dataIndex = i + 1; // 1-based index

                                            // Find the appropriate budget scenario for this data index
                                            const applicableScenario = task.budgetScenarios.find((scenario: any) =>
                                                scenario.isActive &&
                                                dataIndex >= scenario.range.min &&
                                                dataIndex <= scenario.range.max
                                            );

                                            if (applicableScenario) {
                                                const budgetDistribution = applicableScenario.budgetDistribution;
                                                const earningsPerEntry = (budgetDistribution as any)[currentUserBudgetKey] || 0;

                                                calculatedEarnings.totalEarned += earningsPerEntry;

                                                // Check if data is validated/approved
                                                if (dataEntry.isValidated === true) {
                                                    calculatedEarnings.validatedEarnings += earningsPerEntry;
                                                } else {
                                                    calculatedEarnings.pendingEarnings += earningsPerEntry;
                                                }
                                            }
                                        }

                                        dataCount += teDataSubmitted.length;
                                    }

                                    earningsDescription = `Based on ${dataCount} data entries from ${applicableTeamExecutives.length} team executives in your jurisdiction • ${currentUserDesignation?.replace('_', ' ').toUpperCase() || 'N/A'} designation`;
                                }

                                return userAssignment ? (
                                    <div className="mt-8">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                            <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
                                            Your Earnings (Calculated)
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <CurrencyDollarIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                                    </div>
                                                    <div className="ml-4">
                                                        <p className="text-sm font-medium text-green-600 dark:text-green-400">Total Earnings</p>
                                                        <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                                                            ₹{calculatedEarnings.totalEarned.toLocaleString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <CheckCircleIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div className="ml-4">
                                                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Validated Earnings</p>
                                                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                            ₹{calculatedEarnings.validatedEarnings.toLocaleString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <ClockIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                                                    </div>
                                                    <div className="ml-4">
                                                        <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending Earnings</p>
                                                        <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                                                            ₹{calculatedEarnings.pendingEarnings.toLocaleString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Data Submission Stats - Only for Team Executives */}
                                        {currentUserDesignation === 'team_executive' && (
                                            <div className="mt-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Data Submission Statistics</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Data Entries Submitted</p>
                                                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            {userAssignment.dataSubmitted?.length || 0} entries
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">Your Designation</p>
                                                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            {userAssignment.designation?.replace('_', ' ') || 'Not assigned'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Earnings Breakdown - Only for Team Executives */}
                                        {currentUserDesignation === 'team_executive' && task.budgetScenarios && task.budgetScenarios.length > 0 && (
                                            <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                    <ChartBarIcon className="h-5 w-5 text-blue-600 mr-2" />
                                                    Earnings Breakdown by Data Status
                                                </h4>

                                                {/* Data Entries Breakdown */}
                                                {userAssignment.dataSubmitted && userAssignment.dataSubmitted.length > 0 && (
                                                    <div className="mb-6">
                                                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Individual Data Entries:</h5>
                                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                                            {userAssignment.dataSubmitted.map((entry: any, index: number) => {
                                                                const dataIndex = index + 1;
                                                                const applicableScenario = task.budgetScenarios
                                                                    .filter((s: any) => s.isActive)
                                                                    .sort((a: any, b: any) => a.range.min - b.range.min)
                                                                    .find((scenario: any) =>
                                                                        dataIndex >= scenario.range.min &&
                                                                        dataIndex <= scenario.range.max
                                                                    );

                                                                if (!applicableScenario) return null;

                                                                const designationMap: { [key: string]: string } = {
                                                                    'team_executive': 'teamExecutive',
                                                                    'team_leader': 'teamLeader',
                                                                    'district_head': 'districtHead',
                                                                    'state_head': 'stateHead',
                                                                    'regional_head': 'regionalHead',
                                                                    'admin': 'admin'
                                                                };

                                                                const budgetKey = designationMap[userAssignment.designation] || userAssignment.designation;
                                                                const earningsPerEntry = (applicableScenario.budgetDistribution as any)[budgetKey] || 0;
                                                                const isValidated = entry.isValidated === true;

                                                                return (
                                                                    <div key={index} className="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded text-sm">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium">Entry #{dataIndex}</span>
                                                                            <span className={`px-2 py-1 rounded text-xs ${isValidated
                                                                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                                                                }`}>
                                                                                {isValidated ? 'Validated' : 'Pending'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <span className="font-semibold">₹{earningsPerEntry.toLocaleString()}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Summary by Scenario */}
                                                <div className="space-y-3">
                                                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Summary by Budget Scenarios:</h5>
                                                    {task.budgetScenarios
                                                        .filter((s: any) => s.isActive)
                                                        .sort((a: any, b: any) => a.range.min - b.range.min)
                                                        .map((scenario: any, index: number) => {
                                                            const currentDataCount = userAssignment.dataSubmitted?.length || 0;
                                                            const scenarioMin = scenario.range.min;
                                                            const scenarioMax = scenario.range.max;
                                                            const startIndex = Math.max(1, scenarioMin);
                                                            const endIndex = Math.min(currentDataCount, scenarioMax);

                                                            let entriesInScenario = 0;
                                                            if (startIndex <= endIndex) {
                                                                entriesInScenario = endIndex - startIndex + 1;
                                                            }

                                                            const designationMap: { [key: string]: string } = {
                                                                'team_executive': 'teamExecutive',
                                                                'team_leader': 'teamLeader',
                                                                'district_head': 'districtHead',
                                                                'state_head': 'stateHead',
                                                                'regional_head': 'regionalHead',
                                                                'admin': 'admin'
                                                            };

                                                            const budgetKey = designationMap[userAssignment.designation] || userAssignment.designation;
                                                            const earningsPerEntry = (scenario.budgetDistribution as any)[budgetKey] || 0;
                                                            const totalFromScenario = entriesInScenario * earningsPerEntry;

                                                            return (
                                                                <div key={index} className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                                                                    <div>
                                                                        <p className="font-medium text-gray-900 dark:text-white">{scenario.name}</p>
                                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                            Range: {scenarioMin} - {scenarioMax} entries
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className="font-semibold text-gray-900 dark:text-white">
                                                                            {entriesInScenario} entries × ₹{earningsPerEntry}
                                                                        </p>
                                                                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                                                            = ₹{totalFromScenario.toLocaleString()}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : null;
                            })()}

                        </div>
                    )}

                    {activeTab === 'hierarchy' && (
                        <div className="p-8">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                                <UserGroupIcon className="h-5 w-5 text-blue-600 mr-2" />
                                Team Hierarchy & Reporting Structure
                            </h3>

                            {task && user ? (
                                <div className="space-y-6">
                                    {/* Current User's Assignment Info */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                                        <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                            Your Assignment: {task.title}
                                        </h4>
                                        <p className="text-blue-700 dark:text-blue-300">
                                            Company: {task.company.companyName} |
                                            Your Designation: {task.assignedUsers.find(au => au.user?._id === user.id)?.designation?.replace('_', ' ') || 'Not assigned'}
                                        </p>
                                    </div>

                                    {/* Team Hierarchy */}
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        {/* Reporting Manager */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                <span className="h-3 w-3 bg-red-500 rounded-full mr-2"></span>
                                                Reporting Manager
                                            </h5>
                                            {(() => {
                                                const userAssignment = task.assignedUsers.find(au => au.user?._id === user.id);
                                                const userDesignation = userAssignment?.designation;
                                                const userRegion = userAssignment?.user?.region;
                                                const userState = userAssignment?.user?.personalDetails?.address?.state;
                                                const userDistrict = userAssignment?.user?.personalDetails?.address?.district;


                                                let reportingManager = null;

                                                if (userDesignation === 'team_executive') {
                                                    // Team executive reports to team leader in same district
                                                    reportingManager = task.assignedUsers.find(au =>
                                                        au.designation === 'team_leader' &&
                                                        au.user?.personalDetails?.address?.district === userDistrict &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );
                                                } else if (userDesignation === 'team_leader') {
                                                    // Team leader reports to district head in same district
                                                    reportingManager = task.assignedUsers.find(au =>
                                                        au.designation === 'district_head' &&
                                                        au.user?.personalDetails?.address?.district === userDistrict &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );

                                                    // If no district head, check state head
                                                    if (!reportingManager) {
                                                        reportingManager = task.assignedUsers.find(au =>
                                                            au.designation === 'state_head' &&
                                                            au.user?.personalDetails?.address?.state === userState
                                                        );
                                                    }

                                                    // If no state head, check regional head
                                                    if (!reportingManager) {
                                                        reportingManager = task.assignedUsers.find(au =>
                                                            au.designation === 'regional_head' &&
                                                            au.user?.region === userRegion
                                                        );
                                                    }

                                                    // If no regional head, check staff BDM
                                                    if (!reportingManager) {
                                                        const staffBDM = task.assignedUsers.find(au => (au as any).staff && (au as any).staff.role === 'bdm');
                                                        if (staffBDM && (staffBDM as any).staff) {
                                                            reportingManager = {
                                                                user: {
                                                                    firstName: (staffBDM as any).staff.firstName,
                                                                    lastName: (staffBDM as any).staff.lastName,
                                                                    email: (staffBDM as any).staff.email,
                                                                    phone: (staffBDM as any).staff.phone,
                                                                    designation: 'BDM'
                                                                }
                                                            };
                                                        }
                                                    }
                                                } else if (userDesignation === 'district_head') {
                                                    // District head reports to state head in same state
                                                    reportingManager = task.assignedUsers.find(au =>
                                                        au.designation === 'state_head' &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );

                                                    // If no state head, check regional head
                                                    if (!reportingManager) {
                                                        reportingManager = task.assignedUsers.find(au =>
                                                            au.designation === 'regional_head' &&
                                                            au.user?.region === userRegion
                                                        );
                                                    }

                                                    // If no regional head, check staff BDM
                                                    if (!reportingManager) {
                                                        const staffBDM = task.assignedUsers.find(au => (au as any).staff && (au as any).staff.role === 'bdm');
                                                        if (staffBDM && (staffBDM as any).staff) {
                                                            reportingManager = {
                                                                user: {
                                                                    firstName: (staffBDM as any).staff.firstName,
                                                                    lastName: (staffBDM as any).staff.lastName,
                                                                    email: (staffBDM as any).staff.email,
                                                                    phone: (staffBDM as any).staff.phone,
                                                                    designation: 'BDM'
                                                                }
                                                            };
                                                        }
                                                    }
                                                } else if (userDesignation === 'state_head') {
                                                    // State head reports to regional head in same region
                                                    reportingManager = task.assignedUsers.find(au =>
                                                        au.designation === 'regional_head' &&
                                                        au.user?.region === userRegion
                                                    );

                                                    // If no regional head, check staff BDM
                                                    if (!reportingManager) {
                                                        const staffBDM = task.assignedUsers.find(au => (au as any).staff && (au as any).staff.role === 'bdm');
                                                        if (staffBDM && (staffBDM as any).staff) {
                                                            reportingManager = {
                                                                user: {
                                                                    firstName: (staffBDM as any).staff.firstName,
                                                                    lastName: (staffBDM as any).staff.lastName,
                                                                    email: (staffBDM as any).staff.email,
                                                                    phone: (staffBDM as any).staff.phone,
                                                                    designation: 'BDM'
                                                                }
                                                            };
                                                        }
                                                    }
                                                } else if (userDesignation === 'regional_head') {
                                                    // Regional head reports to staff BDM
                                                    const staffBDM = task.assignedUsers.find(au => (au as any).staff && (au as any).staff.role === 'bdm');
                                                    if (staffBDM && (staffBDM as any).staff) {
                                                        reportingManager = {
                                                            user: {
                                                                firstName: (staffBDM as any).staff.firstName,
                                                                lastName: (staffBDM as any).staff.lastName,
                                                                email: (staffBDM as any).staff.email,
                                                                phone: (staffBDM as any).staff.phone,
                                                                designation: 'BDM'
                                                            }
                                                        };
                                                    } else {
                                                        // Fallback to admin
                                                        reportingManager = {
                                                            user: {
                                                                firstName: 'System',
                                                                lastName: 'Administrator',
                                                                email: 'admin@system.com',
                                                                phone: 'N/A',
                                                                designation: 'admin'
                                                            }
                                                        };
                                                    }
                                                }


                                                return reportingManager ? (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                                        <div className="flex items-center space-x-3 mb-3">
                                                            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                                                                <UserGroupIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                                                            </div>
                                                            <div>
                                                                <h6 className="font-semibold text-gray-900 dark:text-white">
                                                                    {reportingManager.user?.firstName} {reportingManager.user?.lastName}
                                                                </h6>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                    {reportingManager.user?.designation?.replace('_', ' ') || 'Administrator'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2 text-sm">
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{reportingManager.user?.email}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{(reportingManager.user as any).phone || 'Not provided'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Region:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{(reportingManager.user as any).region || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-500 dark:text-gray-400">No reporting manager found</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Current User */}
                                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border-2 border-green-200 dark:border-green-800">
                                            <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                <span className="h-3 w-3 bg-green-500 rounded-full mr-2"></span>
                                                You (Current User)
                                            </h5>
                                            {(() => {
                                                const currentUserAssignment = task.assignedUsers.find(au => au.user?._id === user.id);
                                                const currentUser = currentUserAssignment?.user;

                                                return currentUser ? (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                                        <div className="flex items-center space-x-3 mb-3">
                                                            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                                                <UserGroupIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                                            </div>
                                                            <div>
                                                                <h6 className="font-semibold text-gray-900 dark:text-white">
                                                                    {currentUser.firstName} {currentUser.lastName}
                                                                </h6>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                    {currentUserAssignment.designation?.replace('_', ' ') || 'Not assigned'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2 text-sm">
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{currentUser.email}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{(currentUser as any).phone || 'Not provided'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Region:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">{currentUser.region || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 dark:text-gray-400">Location:</span>
                                                                <span className="ml-2 text-gray-900 dark:text-white">
                                                                    {currentUser.personalDetails?.address?.state || 'N/A'}, {currentUser.personalDetails?.address?.district || 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-500 dark:text-gray-400">User assignment not found</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Subordinates */}
                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6">
                                            <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                <span className="h-3 w-3 bg-purple-500 rounded-full mr-2"></span>
                                                Your Team
                                            </h5>
                                            {(() => {
                                                const userAssignment = task.assignedUsers.find(au => au.user?._id === user.id);
                                                const userDesignation = userAssignment?.designation;
                                                const userRegion = userAssignment?.user?.region;
                                                const userState = userAssignment?.user?.personalDetails?.address?.state;
                                                const userDistrict = userAssignment?.user?.personalDetails?.address?.district;


                                                let subordinates: any[] = [];

                                                if (userDesignation === 'team_leader') {
                                                    // Team leader manages team executives in same district
                                                    subordinates = task.assignedUsers.filter(au =>
                                                        au.designation === 'team_executive' &&
                                                        au.user?.personalDetails?.address?.district === userDistrict &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );
                                                } else if (userDesignation === 'district_head') {
                                                    // District head manages team leaders in same district
                                                    subordinates = task.assignedUsers.filter(au =>
                                                        au.designation === 'team_leader' &&
                                                        au.user?.personalDetails?.address?.district === userDistrict &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );
                                                } else if (userDesignation === 'state_head') {
                                                    // State head manages district heads in same state
                                                    subordinates = task.assignedUsers.filter(au =>
                                                        au.designation === 'district_head' &&
                                                        au.user?.personalDetails?.address?.state === userState
                                                    );
                                                } else if (userDesignation === 'regional_head') {
                                                    // Regional head manages state heads in same region
                                                    subordinates = task.assignedUsers.filter(au =>
                                                        au.designation === 'state_head' &&
                                                        au.user?.region === userRegion
                                                    );
                                                }


                                                return subordinates.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {subordinates.map((subordinate) => (
                                                            <div key={subordinate.user._id} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                                                <div className="flex items-center space-x-3 mb-3">
                                                                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                                                                        <UserGroupIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="font-medium text-gray-900 dark:text-white">
                                                                            {subordinate.user.firstName} {subordinate.user.lastName}
                                                                        </h6>
                                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                            {subordinate.designation?.replace('_', ' ')}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="space-y-1 text-xs">
                                                                    <div>
                                                                        <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                                                        <span className="ml-2 text-gray-900 dark:text-white">{subordinate.user.email}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                                                                        <span className="ml-2 text-gray-900 dark:text-white">{subordinate.user.phone || 'Not provided'}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500 dark:text-gray-400">Location:</span>
                                                                        <span className="ml-2 text-gray-900 dark:text-white">
                                                                            {subordinate.user.personalDetails?.address?.state || 'N/A'}, {subordinate.user.personalDetails?.address?.district || 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500 dark:text-gray-400">Status:</span>
                                                                        <span className="ml-2 text-green-600 dark:text-green-400 font-medium">{subordinate.status}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-500 dark:text-gray-400">No team members to manage</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Staff Section */}
                                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6">
                                            <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                                <span className="h-3 w-3 bg-orange-500 rounded-full mr-2"></span>
                                                Task Staff
                                            </h5>
                                            {(() => {
                                                const staffAssignments = task.assignedUsers.filter(au => (au as any).staff);

                                                return staffAssignments.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {staffAssignments.map((staffAssignment) => {
                                                            const staff = (staffAssignment as any).staff;
                                                            if (!staff) return null;
                                                            return (
                                                                <div key={staff._id} className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                                                    <div className="flex items-center space-x-3 mb-3">
                                                                        <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                                                                            <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                                                                                {staff.firstName?.[0] || 'N'}{staff.lastName?.[0] || 'A'}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <h6 className="font-medium text-gray-900 dark:text-white">
                                                                                {staff.firstName} {staff.lastName}
                                                                            </h6>
                                                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                {staff.role?.replace('_', ' ').toUpperCase()}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="space-y-1 text-xs">
                                                                        <div>
                                                                            <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                                                            <span className="ml-2 text-gray-900 dark:text-white">{staff.email}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                                                                            <span className="ml-2 text-gray-900 dark:text-white">{staff.phone || 'Not provided'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-gray-500 dark:text-gray-400">Status:</span>
                                                                            <span className={`ml-2 font-medium ${staff.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                                                {staff.isActive ? 'Active' : 'Inactive'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-500 dark:text-gray-400">No staff assigned to this task</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">Loading team hierarchy data...</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'data' && (
                        <div className="p-8">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                                <ChartBarIcon className="h-5 w-5 text-purple-600 mr-2" />
                                Data Collection & Validation
                            </h3>

                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Required Fields</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {task.validationRules?.requiredFields?.map((field, index) => (
                                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full text-sm">
                                                {field}
                                            </span>
                                        )) || []}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Optional Fields</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {task.validationRules?.optionalFields?.map((field, index) => (
                                            <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-full text-sm">
                                                {field}
                                            </span>
                                        )) || []}
                                    </div>
                                </div>

                                {/* Data Submission for Team Executives */}
                                {user && isUser(user) && user.designation === 'team_executive' && (
                                    <div className="space-y-6">
                                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                                            <h4 className="text-md font-medium text-green-900 dark:text-green-100 mb-3">
                                                Your Data Submissions
                                            </h4>
                                            <p className="text-green-700 dark:text-green-300 mb-4">
                                                As a Team Executive, you can submit data for this task. Click the "Submit Data" button above to add new data entries.
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                    <p className="text-sm text-green-600 dark:text-green-400">Total Submissions</p>
                                                    <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                                                        {task.assignedUsers.find(assignment => assignment.user?._id === user.id)?.dataSubmitted?.length || 0}
                                                    </p>
                                                </div>
                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                    <p className="text-sm text-blue-600 dark:text-blue-400">Validated</p>
                                                    <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                                                        {task.assignedUsers.find(assignment => assignment.user?._id === user.id)?.dataSubmitted?.filter((data: any) => data.isValidated)?.length || 0}
                                                    </p>
                                                </div>
                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                    <p className="text-sm text-purple-600 dark:text-purple-400">Your Earnings</p>
                                                    <p className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                                                        ₹{(() => {
                                                            const userAssignment = task.assignedUsers.find(assignment => assignment.user?._id === user.id);
                                                            const calculatedEarnings = userAssignment ? calculateEarnings(task, userAssignment) : { totalEarned: 0 };
                                                            return calculatedEarnings.totalEarned.toLocaleString();
                                                        })()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Submitted Data Table */}
                                        {(() => {
                                            const userAssignment = task.assignedUsers.find(assignment => assignment.user?._id === user.id);
                                            console.log('User assignment:', userAssignment);
                                            console.log('Data submitted:', userAssignment?.dataSubmitted);
                                            return (userAssignment?.dataSubmitted?.length || 0) > 0;
                                        })() && (
                                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            Submitted Data Entries
                                                        </h4>
                                                    </div>
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                                <tr>
                                                                    {task.validationRules?.requiredFields?.map((field, index) => (
                                                                        <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            {field}
                                                                        </th>
                                                                    )) || []}
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                        Submitted At
                                                                    </th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                        Status
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                                {task.assignedUsers.find(assignment => assignment.user?._id === user.id)?.dataSubmitted
                                                                    ?.sort((a: any, b: any) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
                                                                    ?.map((entry: any, index: number) => {
                                                                        console.log('Entry being rendered:', entry);
                                                                        return (
                                                                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                                                {task.validationRules?.requiredFields?.map((field, fieldIndex) => {
                                                                                    console.log(`Field ${field}:`, entry.data?.[field]);
                                                                                    return (
                                                                                        <td key={fieldIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                                            {entry.data?.[field] || '-'}
                                                                                        </td>
                                                                                    );
                                                                                }) || []}
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                                    {entry.submittedAt ? new Date(entry.submittedAt).toLocaleString() : '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${entry.isValidated
                                                                                        ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                                                                        }`}>
                                                                                        {entry.isValidated ? 'Validated' : 'Pending'}
                                                                                    </span>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }) || []}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                )}

                                {/* Hierarchical Data Collection for Non-Team Executives */}
                                {user && isUser(user) && user.designation !== 'team_executive' && (() => {
                                    const userAssignment = task.assignedUsers.find(assignment => assignment.user?._id === user.id);
                                    if (!userAssignment) return null;

                                    const currentUserDesignation = userAssignment.user?.designation;
                                    const currentUserRegion = userAssignment.user?.region;
                                    const currentUserAddress = userAssignment.user?.personalDetails?.address;

                                    // Filter team executives based on hierarchical level
                                    const applicableTeamExecutives = task.assignedUsers.filter(assignment => {
                                        const teUser = assignment.user;
                                        const teDesignation = teUser?.designation;

                                        // Must be team executive with data submitted
                                        if (teDesignation !== 'team_executive' || (assignment.dataSubmitted?.length || 0) === 0) {
                                            return false;
                                        }

                                        // Location matching based on current user's designation
                                        if (currentUserDesignation === 'team_leader' || currentUserDesignation === 'district_head') {
                                            // Match district + state
                                            return teUser?.personalDetails?.address?.district === currentUserAddress?.district &&
                                                teUser?.personalDetails?.address?.state === currentUserAddress?.state;
                                        } else if (currentUserDesignation === 'state_head') {
                                            // Match state + region
                                            return teUser?.personalDetails?.address?.state === currentUserAddress?.state &&
                                                teUser?.region === currentUserRegion;
                                        } else if (currentUserDesignation === 'regional_head') {
                                            // Match region only
                                            return teUser?.region === currentUserRegion;
                                        }

                                        return false;
                                    });

                                    const totalDataEntries = applicableTeamExecutives.reduce((total, assignment) =>
                                        total + (assignment.dataSubmitted?.length || 0), 0
                                    );

                                    const validatedEntries = applicableTeamExecutives.reduce((total, assignment) =>
                                        total + (assignment.dataSubmitted?.filter((data: any) => data.isValidated)?.length || 0), 0
                                    );

                                    const pendingEntries = totalDataEntries - validatedEntries;

                                    return (
                                        <div className="space-y-6">
                                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
                                                <h4 className="text-md font-medium text-purple-900 dark:text-purple-100 mb-3">
                                                    Team Data Collection Summary
                                                </h4>
                                                <p className="text-purple-700 dark:text-purple-300 mb-4">
                                                    Data submitted by team executives under your jurisdiction as a {currentUserDesignation?.replace('_', ' ').toUpperCase()}.
                                                </p>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                        <p className="text-sm text-purple-600 dark:text-purple-400">Total Data Entries</p>
                                                        <p className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                                                            {totalDataEntries}
                                                        </p>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                        <p className="text-sm text-green-600 dark:text-green-400">Validated</p>
                                                        <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                                                            {validatedEntries}
                                                        </p>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                                        <p className="text-sm text-yellow-600 dark:text-yellow-400">Pending</p>
                                                        <p className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                                                            {pendingEntries}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 text-center">
                                                    <p className="text-sm text-purple-600 dark:text-purple-400">
                                                        From {applicableTeamExecutives.length} team executives in your jurisdiction
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Team Executives Data Table */}
                                            {applicableTeamExecutives.length > 0 && (
                                                <div className="space-y-4">
                                                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                                        Data Submitted by Team Executives
                                                    </h4>
                                                    {applicableTeamExecutives.map((assignment) => {
                                                        const teUser = assignment.user;
                                                        const dataSubmitted = assignment.dataSubmitted || [];

                                                        return (
                                                            <div key={`assignment-${teUser?._id}-${assignment.assignedAt}`} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                                                    <div className="flex items-center justify-between">
                                                                        <h5 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                                            {teUser?.firstName} {teUser?.lastName}
                                                                        </h5>
                                                                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                                                                            <span>{dataSubmitted.length} entries</span>
                                                                            <span>{dataSubmitted.filter((data: any) => data.isValidated).length} validated</span>
                                                                            <span>{dataSubmitted.filter((data: any) => !data.isValidated).length} pending</span>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                        {teUser?.email} • {teUser?.personalDetails?.address?.district}, {teUser?.personalDetails?.address?.state}
                                                                    </p>
                                                                </div>
                                                                {dataSubmitted.length > 0 && (
                                                                    <div className="overflow-x-auto">
                                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                                                <tr>
                                                                                    {task.validationRules?.requiredFields?.map((field, index) => (
                                                                                        <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                                            {field}
                                                                                        </th>
                                                                                    )) || []}
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                                        Submitted At
                                                                                    </th>
                                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                                        Status
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                                                {dataSubmitted
                                                                                    ?.sort((a: any, b: any) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
                                                                                    ?.map((data: any, index: number) => (
                                                                                        <tr key={index}>
                                                                                            {task.validationRules?.requiredFields?.map((field, fieldIndex) => (
                                                                                                <td key={fieldIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                                                    {data.data[field] || '-'}
                                                                                                </td>
                                                                                            )) || []}
                                                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                                                {new Date(data.submittedAt).toLocaleString()}
                                                                                            </td>
                                                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${data.isValidated
                                                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                                                                                    }`}>
                                                                                                    {data.isValidated ? 'Validated' : 'Pending'}
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Data Submission Modal for Team Executives */}
            {showDataSubmissionModal && user && isUser(user) && user.designation === 'team_executive' && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl container w-full min-h-[90vh] max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Submit Data - {task.title}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowDataSubmissionModal(false);
                                    setSubmissionData([]);
                                    setCsvFile(null);
                                    setCsvData([]);
                                    setShowCsvPreview(false);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Required Fields: {task.validationRules?.requiredFields?.join(', ') || 'None specified'}
                                </h3>

                                {/* CSV Upload Section */}
                                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                        Upload CSV File
                                    </h4>
                                    <div className="flex items-center gap-4">
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleCsvFileUpload}
                                            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                                        />
                                        <button
                                            onClick={addDataRow}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Add Row
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        Upload a CSV file with headers matching the required fields, or add data manually.
                                    </p>
                                </div>

                                {/* CSV Preview */}
                                {showCsvPreview && csvData.length > 0 && (
                                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-md font-medium text-blue-900 dark:text-blue-100">
                                                CSV Preview ({csvData.length} rows)
                                            </h4>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={useCsvData}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                                                >
                                                    Use This Data
                                                </button>
                                                <button
                                                    onClick={clearCsvData}
                                                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                                                <thead>
                                                    <tr className="bg-gray-50 dark:bg-gray-700">
                                                        {task.validationRules?.requiredFields?.map((field) => (
                                                            <th key={field} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                {field}
                                                            </th>
                                                        )) || []}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                    {csvData.slice(0, 5).map((row, index) => (
                                                        <tr key={index}>
                                                            {task.validationRules?.requiredFields?.map((field) => (
                                                                <td key={field} className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                                                    {row[field] || '-'}
                                                                </td>
                                                            )) || []}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {csvData.length > 5 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                Showing first 5 rows of {csvData.length} total rows
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {submissionData.length > 0 && (
                                <div className="space-y-4">
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                        Data Entries ({submissionData.length})
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                                            <thead>
                                                <tr className="bg-gray-50 dark:bg-gray-700">
                                                    {task.validationRules?.requiredFields?.map((field) => (
                                                        <th key={field} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                            {field}
                                                        </th>
                                                    )) || []}
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                {submissionData.map((row, index) => (
                                                    <tr key={index}>
                                                        {task.validationRules?.requiredFields?.map((field) => (
                                                            <td key={field} className="px-4 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={row[field] || ''}
                                                                    onChange={(e) => updateDataRow(index, field, e.target.value)}
                                                                    className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                    placeholder={`Enter ${field}`}
                                                                />
                                                            </td>
                                                        )) || []}
                                                        <td className="px-4 py-2">
                                                            <button
                                                                onClick={() => removeDataRow(index)}
                                                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                            >
                                                                <XMarkIcon className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowDataSubmissionModal(false);
                                        setSubmissionData([]);
                                        setCsvFile(null);
                                        setCsvData([]);
                                        setShowCsvPreview(false);
                                    }}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDataSubmission}
                                    disabled={submissionData.length === 0}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    <CloudArrowUpIcon className="h-4 w-4" />
                                    Submit Data ({submissionData.length} entries)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTaskDetails;
